import { httpService } from '../utils/http';
import { 
  PackageGroup, 
  PackageGroupRequest, 
  PackageGroupQueryRequest, 
  PackageGroupVO,
  PaginationParams,
  PaginatedResponse, 
  Page
} from '../types';

/**
 * 套餐组服务类
 * 处理套餐组相关的 API 请求
 */
export class PackageGroupService {


  /**
   * 使用DTO创建套餐组
   * @param groupData 套餐组数据
   * @param createBy 创建人
   * @returns Promise<PackageGroup>
   */
  async createPackageGroupWithDto(groupData: PackageGroupRequest, createBy: string): Promise<PackageGroup> {
    const response = await httpService.post(`/package/group/create?createBy=${encodeURIComponent(createBy)}`, groupData);
    return response.data;
  }

  /**
   * 更新套餐组
   * @param id 套餐组ID
   * @param groupData 套餐组数据
   * @returns Promise<PackageGroup>
   */
  async updatePackageGroup(id: number, groupData: PackageGroupRequest): Promise<PackageGroup> {
    const response = await httpService.post(`/package/group/${id}/update`, groupData);
    return response.data;
  }

  /**
   * 使用DTO更新套餐组
   * @param id 套餐组ID
   * @param groupData 套餐组数据
   * @param updateBy 更新人
   * @returns Promise<PackageGroup>
   */
  async updatePackageGroupWithDto(id: number, groupData: PackageGroupRequest, updateBy: string): Promise<PackageGroup> {
    const response = await httpService.post(`/package/group/${id}/update?updateBy=${encodeURIComponent(updateBy)}`, groupData);
    return response.data;
  }

  /**
   * 获取套餐组详情
   * @param id 套餐组ID
   * @returns Promise<PackageGroup>
   */
  async getPackageGroupById(id: number): Promise<PackageGroup> {
    const response = await httpService.get(`/package/group/${id}`);
    return response.data;
  }

  /**
   * 分页查询套餐组
   * @param params 分页参数
   * @returns Promise<PaginatedResponse<PackageGroup>>
   */
  async getPackageGroups(params: PaginationParams): Promise<PaginatedResponse<PackageGroup>> {
    const response = await httpService.get('/package/group', params);
    return response.data;
  }

  /**
   * 根据设备类型查询套餐组
   * @param deviceType 设备类型
   * @returns Promise<PackageGroup[]>
   */
  async getPackageGroupsByDeviceType(deviceType: string): Promise<PackageGroup[]> {
    const response = await httpService.get(`/package/group/device-type/${deviceType}`);
    return response.data;
  }

  /**
   * 根据经销商查询套餐组
   * @param dealerId 经销商ID
   * @returns Promise<PackageGroup[]>
   */
  async getPackageGroupsByDealer(dealerId: number): Promise<PackageGroup[]> {
    const response = await httpService.get(`/package/group/dealer/${dealerId}`);
    return response.data;
  }

  /**
   * 查询公共套餐组
   * @returns Promise<PackageGroup[]>
   */
  async getPublicPackageGroups(): Promise<Page<PackageGroup>> {
    const response = await httpService.get('/package/group/public');
    return response.data;
  }

  /**
   * 条件查询套餐组
   * @param queryRequest 查询条件
   * @returns Promise<PackageGroupVO[]>
   */
  async queryPackageGroups(queryRequest: PackageGroupQueryRequest): Promise<Page<PackageGroupVO>> {
    const response = await httpService.post('/package/group/query', queryRequest);
    return response.data;
  }

  /**
   * 启用套餐组
   * @param id 套餐组ID
   * @param updateBy 更新人
   * @returns Promise<void>
   */
  async enablePackageGroup(id: number, updateBy: string): Promise<void> {
    await httpService.post(`/package/group/${id}/enable?updateBy=${encodeURIComponent(updateBy)}`);
  }

  /**
   * 禁用套餐组
   * @param id 套餐组ID
   * @param updateBy 更新人
   * @returns Promise<void>
   */
  async disablePackageGroup(id: number, updateBy: string): Promise<void> {
    await httpService.post(`/package/group/${id}/disable?updateBy=${encodeURIComponent(updateBy)}`);
  }

  /**
   * 删除套餐组
   * @param id 套餐组ID
   * @param updateBy 删除人
   * @returns Promise<void>
   */
  async deletePackageGroup(id: number, updateBy: string): Promise<void> {
    await httpService.post(`/package/group/${id}/delete?updateBy=${encodeURIComponent(updateBy)}`);
  }
}

// 创建默认实例
export const packageGroupService = new PackageGroupService();

// 导出默认实例
export default packageGroupService; 